import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'

import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SalaryConfig, useSalary } from 'modules/job_catalog/components/SalaryModal/useSalary'
import useAssignJobCatalogTreeNodeAttributes, {
  serializeSalary,
} from 'modules/job_catalog/hooks/useAssignJobCatalogTreeNodeAttributes'

interface Props {
  onClose: () => void
  onSubmit: () => void
  name: string
  nodeType: string
  initialSalary?: SalaryConfig
}

const currency = 'EUR'

const SalaryModal = ({ name, onClose, onSubmit, nodeType, initialSalary }: Props) => {
  const { rootId, familyId, functionId, roleId, levelId, id } = useParams()
  const nodeId = levelId || roleId || functionId || familyId || rootId || id || ''
  const { company } = useCurrentSession()

  const title = i18n.t('job_catalog.nodes.salary_modal.title', { name })
  const finalType = useMemo(() => nodeType.replace('root', 'company'), [nodeType])
  const description = i18n.t('job_catalog.nodes.salary_modal.description', { type: finalType })

  const {
    minAmount,
    maxAmount,
    grossSalary,
    periodicity,
    salaryPeriodicityOptions,
    type,
    salaryTypeOptions,
    isTypeAvailable,
    isTypeRange,
    isValid,
    errors,
    setMinAmount,
    setMaxAmount,
    setGrossSalary,
    setPeriodicity,
    setType,
  } = useSalary(nodeType, initialSalary)

  const { assignAttributes } = useAssignJobCatalogTreeNodeAttributes()
  const saveSalary = async () => {
    const result = await assignAttributes('salary_range', company.id, nodeId, [
      serializeSalary({
        ...(type === 'range' && { min: minAmount }),
        ...(type === 'range' && { max: maxAmount }),
        ...(type === 'fixed' && { gross: grossSalary }),
        currency,
      }),
    ])
    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.salary_modal.success'),
        time: 5000,
      })
    }
    onSubmit()
  }

  return (
    <Modal
      illustration={'money-bag'}
      title={title}
      description={description}
      onClose={onClose}
      onSubmit={() => {}}
      footer={
        <Box gap="s16" flexDirection="row" alignItems="center">
          <Button
            onClick={onClose}
            label={i18n.t('job_catalog.nodes.salary_modal.cancel')}
            variant="outline"
          />
          <Button
            variant="default"
            onClick={saveSalary}
            label={i18n.t('job_catalog.nodes.salary_modal.submit')}
            disabled={!isValid}
          />
        </Box>
      }
    >
      <Box gap="s24">
        {isTypeAvailable && (
          <Select
            label={i18n.t('job_catalog.nodes.salary_modal.type')}
            options={salaryTypeOptions}
            value={type}
            onChange={setType}
          />
        )}
        <Select
          label={i18n.t('job_catalog.nodes.salary_modal.periodicity')}
          options={salaryPeriodicityOptions}
          value={periodicity}
          onChange={setPeriodicity}
          readonly
        />
        {isTypeRange ? (
          <Box flexDirection="row" gap="s24">
            <MoneyField
              label={i18n.t('job_catalog.nodes.salary_modal.min_amount')}
              currency={currency}
              value={minAmount}
              onChange={setMinAmount}
              error={errors.minAmount}
            />
            <MoneyField
              label={i18n.t('job_catalog.nodes.salary_modal.max_amount')}
              currency={currency}
              value={maxAmount}
              onChange={setMaxAmount}
              error={errors.maxAmount}
            />
          </Box>
        ) : (
          <MoneyField
            label={i18n.t('job_catalog.nodes.salary_modal.gross_salary')}
            currency={currency}
            value={grossSalary}
            onChange={setGrossSalary}
            error={errors.grossSalary}
          />
        )}
      </Box>
    </Modal>
  )
}

export default SalaryModal
